import { Box, Grid, CircularProgress } from '@mui/material';
import * as React from 'react';
import { httpGet } from '../../HttpRequestHandling/httpRequestHandler';
import { toast } from 'react-toastify';
import { isNewUi } from '../../redesign/uiMode';

const CALL_LETTER_MANUAL = '__manual__';

export function MediaDetailsFields(props) {

	const [mediaTypeOptions, setMediaTypeOptions] = React.useState([]);
	const [mediaTypeBandMap, setMediaTypeBandMap] = React.useState({});
	const [filteredBandOptions, setFilteredBandOptions] = React.useState([]);
	const [callLetterManualMode, setCallLetterManualMode] = React.useState(false);
	const [isCallLetterInvalid, setIsCallLetterInvalid] = React.useState(false);
	const [isCallLetterValidating, setIsCallLetterValidating] = React.useState(false);
	const autoCallLetterValidationKeyRef = React.useRef('');
	const callLetterValidationRequestIdRef = React.useRef(0);
	const skipAutoCallLetterValidationKeyRef = React.useRef('');

	React.useEffect(() => {
		if (!props.companyCode || props.modelDefId !== 3) return;

		httpGet(`/api/invoicedocument/mediatypebands/${props.companyCode}`)
			.then((response) => {
				const data = response.data;
				const mediaMap = {};
				const mediaList = [];

				data.forEach(item => {
					if (!mediaMap[item.mediaTypeCode]) {
						mediaMap[item.mediaTypeCode] = [];
						mediaList.push({
							value: item.mediaTypeCode,
							label: item.mediaTypeName
						});
					}
					mediaMap[item.mediaTypeCode].push(item.mediaTypeBand);
				});

				setMediaTypeOptions(mediaList);
				setMediaTypeBandMap(mediaMap);

				const selectedMediaType = props.mappedFields['mediaType']?.value;
				const allBands = Array.from(new Set(data.map(item => item.mediaTypeBand)));
				const bandDropdown = (bands) => [{ value: '', label: 'Select Band' }, ...bands.map(b => ({ value: b, label: b }))];

				if (selectedMediaType && mediaMap[selectedMediaType]) {
					setFilteredBandOptions(bandDropdown(mediaMap[selectedMediaType]));
				} else {
					setFilteredBandOptions(bandDropdown(allBands));
				}

			})
			.catch(() => toast.error("Failed to load media type and band options"));
	}, [props.companyCode]);

	const sortByKey = (arr) => {
		const seen = new Set();
		return [...(arr || [])]
			.filter((item) => {
				const normalizedKey = (item?.Key ?? '').toString().trim().toLowerCase();
				if (seen.has(normalizedKey)) return false;
				seen.add(normalizedKey);
				return true;
			})
			.sort((a, b) => (a.Key || '').toString().localeCompare((b.Key || '').toString(), undefined, { sensitivity: 'base', numeric: true }));
	};
	const sortedKeyValues = React.useMemo(() => sortByKey(props.fieldData?.KeyValues), [props.fieldData?.KeyValues]);

	// Key shown in the dropdown — predefined uses the value itself; key-value uses the key name
	const bandSelectValue =
		props.mappedFields['band']?.value ||
		props.mappedFields['bandKeyValue']?.mappedField ||
		'';

	// Actual resolved value shown in the read-only input next to the band dropdown
	const bandDisplayValue =
		props.mappedFields['band']?.value ||
		props.mappedFields['bandKeyValue']?.value ||
		'';

	const callLetterManual = callLetterManualMode || (!!props.mappedFields['callLetter']?.value && !props.mappedFields['callLetterKeyValue']?.mappedField);

	// Key shown in the call letter dropdown
	const callLetterSelectValue = props.mappedFields['callLetterKeyValue']?.mappedField || (callLetterManual ? CALL_LETTER_MANUAL : '');

	// Actual resolved value shown in the call letter input
	const callLetterDisplayValue =
		props.mappedFields['callLetter']?.value ||
		props.mappedFields['callLetterKeyValue']?.value ||
		'';
	const bandDisplayValueForValidation =
		props.mappedFields['band']?.value ||
		props.mappedFields['bandKeyValue']?.value ||
		'';

	const validateCallLetter = React.useCallback(async ({ event = null, showErrorToast = false, showInlineLoader = false, callLetterOverride = null, bandOverride = null } = {}) => {
		const requestId = ++callLetterValidationRequestIdRef.current;

		if (props.modelDefId !== 3) {
			setIsCallLetterValidating(false);
			setIsCallLetterInvalid(false);
			return true;
		}

		if (props.readOnly) {
			setIsCallLetterValidating(false);
			setIsCallLetterInvalid(false);
			return true;
		}

		const callLetterValue = String(callLetterOverride ?? event?.currentTarget?.value ?? callLetterDisplayValue ?? '').trim();
		const vendorCode = String(props.vendorCode || '').trim();
		const bandValue = String(bandOverride ?? bandDisplayValueForValidation ?? '').trim();
		const invoiceDocumentId = Number(props.invoiceDocumentId || 0);

		if (!callLetterValue) {
			setIsCallLetterValidating(false);
			setIsCallLetterInvalid(true);
			return false;
		}

		if (!vendorCode) {
			setIsCallLetterValidating(false);
			setIsCallLetterInvalid(true);
			return false;
		}

		if (showInlineLoader) {
			setIsCallLetterValidating(true);
		}

		try {
			const response = await httpGet(
				`/api/invoicedocument/validatecallletters/${invoiceDocumentId}`
				+ `?modelDefId=${props.modelDefId}`
				+ `&vendorCode=${encodeURIComponent(vendorCode)}`
				+ `&callLetters=${encodeURIComponent(callLetterValue)}`
				+ `&band=${encodeURIComponent(bandValue)}`
			);

			const isValid = Number(response?.data) === 1 || response?.data === true || response?.data === 'true';
			if (requestId !== callLetterValidationRequestIdRef.current) {
				return !!isValid;
			}

			setIsCallLetterInvalid(!isValid);

			if (!isValid && showErrorToast) {
				toast.error(<div>Call Letters are invalid</div>);
			}

			return !!isValid;
		} catch (error) {
			if (requestId !== callLetterValidationRequestIdRef.current) {
				return false;
			}

			setIsCallLetterInvalid(true);
			if (showErrorToast) {
				toast.error(<div>Call Letters validation failed<br />{error.response?.data}</div>);
			}
			return false;
		} finally {
			if (showInlineLoader && requestId === callLetterValidationRequestIdRef.current) {
				setIsCallLetterValidating(false);
			}
		}
	}, [props.modelDefId, props.readOnly, props.vendorCode, props.invoiceDocumentId, callLetterManual, callLetterDisplayValue, bandDisplayValueForValidation]);

	React.useEffect(() => {
		if (typeof props.registerCallLetterValidator !== 'function') {
			return;
		}

		props.registerCallLetterValidator(validateCallLetter);
		return () => props.registerCallLetterValidator(null);
	}, [props.registerCallLetterValidator, validateCallLetter]);

	React.useEffect(() => {
		if (props.modelDefId !== 3 || props.readOnly) {
			return;
		}

		const currentKey = [
			String(props.invoiceDocumentId || ''),
			String(props.vendorCode || ''),
			String(callLetterDisplayValue || ''),
			String(bandDisplayValueForValidation || '')
		].join('|');

		if (skipAutoCallLetterValidationKeyRef.current === currentKey) {
			skipAutoCallLetterValidationKeyRef.current = '';
			return;
		}

		if (autoCallLetterValidationKeyRef.current === currentKey) {
			return;
		}

		autoCallLetterValidationKeyRef.current = currentKey;
		validateCallLetter({ showErrorToast: false, showInlineLoader: !callLetterManual });
	}, [
		props.modelDefId,
		props.readOnly,
		props.invoiceDocumentId,
		props.vendorCode,
		callLetterDisplayValue,
		bandDisplayValueForValidation,
		validateCallLetter
	]);

	const normalizeText = React.useCallback((value) => String(value ?? '').trim().toLowerCase(), []);
	const selectedMediaType = props.mappedFields['mediaType']?.value || '';
	const isBandCompatibleWithMediaType = React.useMemo(() => {
		if (props.modelDefId !== 3) return true;
		if (!selectedMediaType || !bandDisplayValue) return true;

		const allowedBands = mediaTypeBandMap[selectedMediaType] || [];
		if (allowedBands.length === 0) return true;

		const normalizedBand = normalizeText(bandDisplayValue);
		return allowedBands.some((band) => normalizeText(band) === normalizedBand);
	}, [props.modelDefId, selectedMediaType, bandDisplayValue, mediaTypeBandMap, normalizeText]);
	const showBandInvalidState = Boolean(selectedMediaType && bandDisplayValue && !isBandCompatibleWithMediaType);

	React.useLayoutEffect(() => {
		if (typeof props.onBandValidationChange !== 'function') {
			return;
		}

		props.onBandValidationChange({
			isValid: !showBandInvalidState,
			message: showBandInvalidState
				? 'Incorrect band. This band does not belong to the selected media type.'
				: ''
		});
	}, [props.onBandValidationChange, showBandInvalidState, selectedMediaType, bandDisplayValue, mediaTypeBandMap]);

	const handleChange = (field) => (event) => {
		const value = event.target.value;

		if (field === 'mediaType') {
			const allBands = Array.from(new Set(Object.values(mediaTypeBandMap).flat()));
			const bands = value ? mediaTypeBandMap[value] || [] : allBands;
			setFilteredBandOptions([{ value: '', label: 'Select Band' }, ...bands.map(b => ({ value: b, label: b }))]);
		}

		props.field_changehandler({
			event: { target: { value: value } },
			value: field
		});
	};

	// Grouped band dropdown handler
	const handleBandChange = (event) => {
		const value = event.target.value;
		let shouldValidateCallLetter = !props.readOnly;
		let nextBandValueForValidation = '';

		if (!value) {
			props.field_changehandler({ event: { target: { value: '' } }, value: 'band' });
			props.field_changehandler({ event: { target: { value: '' } }, value: 'bandKeyValue' });
			nextBandValueForValidation = '';
		} else {
			const isPredefined = filteredBandOptions.some(opt => opt.value === value);

			if (isPredefined) {
				props.field_changehandler({ event: { target: { value: value } }, value: 'band' });
				props.field_changehandler({ event: { target: { value: '' } }, value: 'bandKeyValue' });
				nextBandValueForValidation = value;
			} else {
				props.field_changehandler({ event: { target: { value: '' } }, value: 'band' });
				props.field_changehandler({ event: { target: { value: value } }, value: 'bandKeyValue' });
				nextBandValueForValidation = sortedKeyValues.find(kv => kv.Key === value)?.Value || value;
			}
		}

		autoCallLetterValidationKeyRef.current = '';
		if (shouldValidateCallLetter) {
			skipAutoCallLetterValidationKeyRef.current = [
				String(props.invoiceDocumentId || ''),
				String(props.vendorCode || ''),
				String(callLetterDisplayValue || '').trim(),
				String(nextBandValueForValidation || '')
			].join('|');
			setTimeout(() => {
				validateCallLetter({
					showErrorToast: false,
					showInlineLoader: true,
					callLetterOverride: String(callLetterDisplayValue || '').trim(),
					bandOverride: nextBandValueForValidation
				});
			}, 0);
		}
	};

	// Grouped call letter dropdown handler
	const handleCallLetterSelectChange = (event) => {
		const value = event.target.value;
		setIsCallLetterInvalid(false);
		setIsCallLetterValidating(false);

		if (!value) {
			// Reset — clear everything and go back to read-only
			setCallLetterManualMode(false);
			props.field_changehandler({ event: { target: { value: '' } }, value: 'callLetter' });
			props.field_changehandler({ event: { target: { value: '' } }, value: 'callLetterKeyValue' });
			return;
		}

		if (value === CALL_LETTER_MANUAL) {
			// "Enter Manually" selected — make input editable, clear any key-value mapping
			setCallLetterManualMode(true);
			props.field_changehandler({ event: { target: { value: '' } }, value: 'callLetterKeyValue' });
		} else {
			// Key Value Pairs group — store key name in callLetterKeyValue, resolved value will come from field_changehandler
			setCallLetterManualMode(false);
			props.field_changehandler({ event: { target: { value: '' } }, value: 'callLetter' });
			props.field_changehandler({ event: { target: { value: value } }, value: 'callLetterKeyValue' });
			if (!props.readOnly) {
				setTimeout(() => {
					validateCallLetter({ showErrorToast: false, showInlineLoader: true });
				}, 0);
			}
		}
	};

	// Manual text input handler for call letter
	const handleCallLetterInputChange = (event) => {
		setIsCallLetterInvalid(false);
		setIsCallLetterValidating(false);
		props.field_changehandler({
			event: { target: { value: event.target.value } },
			value: 'callLetter'
		});
	};

	const callLetter_blurHandler = async (event) => {
		const shouldSkipBlurValidation =
			props.skipCallLetterBlurRef?.current ||
			!!event?.relatedTarget?.closest?.('[data-skip-call-letters-blur="true"]');

		if (shouldSkipBlurValidation) {
			if (props.skipCallLetterBlurRef) {
				props.skipCallLetterBlurRef.current = false;
			}
			return true;
		}

		if (props.readOnly) {
			setIsCallLetterValidating(false);
			return true;
		}

		return await validateCallLetter({ event, showErrorToast: false, showInlineLoader: true });
	};

	return (
		<div className="mf-12">
			<Box>
				{props.modelDefId === 3 && (
					<>
							<Grid container
								spacing={props.style.mainRowSpacing}
								sx={props.style.mainRowStyle}>
								<Grid item
									xs={props.style.cellOneWidth}
									sx={props.style.cellOneStyle}>
									<label>Call Letters</label>
								</Grid>
								<Grid item
									xs={props.style.cellTwoWidth}
									sx={props.style.cellTwoStyle}
									style={{ display: props.advanceEdit ? 'block' : 'none' }}>
									<select
														value={callLetterSelectValue}
														onChange={handleCallLetterSelectChange}
														disabled={props.readOnly}
														className={!callLetterDisplayValue ? 'border-red' : ''}
														style={{ width: '100%', ...props.style.inputStyle }}
													>
										<option value="">{isNewUi() ? 'Not mapped' : 'Select Option'}</option>
										<optgroup label="Enter Manually">
														<option value={CALL_LETTER_MANUAL}>Manual Option</option>
													</optgroup>
										{sortedKeyValues.length > 0 && (
											<optgroup label="Key Value Pairs">
												{sortedKeyValues.map((kv, i) => (
													<option key={'kv-cl-' + i} value={kv.Key}>{kv.Key}</option>
												))}
											</optgroup>
										)}
									</select>
								</Grid>
								<Grid item
									xs={props.style.cellThreeWidth}
									sx={props.style.cellThreeStyle}>
															<div className="textBoxValidate" style={{ position: 'relative' }}>
																<input
																	type="text"
																	value={callLetterDisplayValue}
																	readOnly={!callLetterManual || props.readOnly}
																	onChange={handleCallLetterInputChange}
																	onBlur={callLetter_blurHandler}
																	maxLength={4}
											className={(isCallLetterInvalid || !callLetterDisplayValue) ? 'border-red' : ''}
																	style={{ width: '100%', paddingRight: isCallLetterValidating ? 32 : undefined, ...props.style.inputStyle }}
																/>
																{isCallLetterValidating && (
																	<span
																		style={{
																			position: 'absolute',
																			right: 10,
																			top: '50%',
																			transform: 'translateY(-50%)',
																			display: 'flex',
																			alignItems: 'center',
																			pointerEvents: 'none'
																		}}
																	>
																		<CircularProgress size={16} thickness={5} />
																	</span>
																)}
															</div>
								</Grid>
							</Grid>

							<Grid container
								spacing={props.style.mainRowSpacing}
								sx={props.style.mainRowStyle}>
								<Grid item xs={props.style.cellOneWidth}
									sx={props.style.cellOneStyle}>
									<label>Media Type</label>
								</Grid>
								<Grid item
									xs={props.style.cellTwoWidth}
									sx={props.style.cellTwoStyle}>
									<select
														value={props.mappedFields['mediaType']?.value || ''}
														onChange={handleChange('mediaType')}
														disabled={props.readOnly}
														className={!props.mappedFields['mediaType']?.value ? 'border-red' : ''}
														style={{ width: '100%', ...props.style.inputStyle }}
													>
										<option value="">Select Media Type</option>
										{mediaTypeOptions.map((opt) => (
											<option key={opt.value} value={opt.value}>{opt.label}</option>
										))}
									</select>
								</Grid>
							</Grid>

							<Grid container
								spacing={props.style.mainRowSpacing}
								sx={props.style.mainRowStyle}>
								<Grid item xs={props.style.cellOneWidth}
									sx={props.style.cellOneStyle}>
									<label>Band</label>
								</Grid>
								<Grid item
									xs={props.style.cellTwoWidth}
									sx={props.style.cellTwoStyle}
									style={{ display: props.advanceEdit ? 'block' : 'none' }}>
									<select
														value={bandSelectValue}
														onChange={handleBandChange}
														disabled={props.readOnly}
														className={!bandDisplayValue ? 'border-red' : ''}
														style={{ width: '100%', ...props.style.inputStyle }}
													>
										<option value="">Select Band</option>
										{filteredBandOptions.filter(opt => opt.value !== '').length > 0 && (
											<optgroup label="Predefined Bands">
												{filteredBandOptions
													.filter(opt => opt.value !== '')
													.map(opt => (
														<option key={opt.value} value={opt.value}>{opt.label}</option>
													))}
											</optgroup>
										)}
										{sortedKeyValues.length > 0 && (
											<optgroup label="Key Value Pairs">
												{sortedKeyValues.map((kv, i) => (
													<option key={'kv-band-' + i} value={kv.Key}>{kv.Key}</option>
												))}
											</optgroup>
										)}
									</select>
								</Grid>
								<Grid item
									xs={props.style.cellThreeWidth}
									sx={props.style.cellThreeStyle}>
									<input
										type="text"
										readOnly
										value={bandDisplayValue}
									className={(showBandInvalidState || !bandDisplayValue) ? 'border-red' : ''}
										style={{ width: '100%', ...props.style.inputStyle }}
									/>
								</Grid>
							</Grid>


						</>
				)}
			</Box>
		</div>
	);
}


